import React, { useState, useEffect, useRef, useMemo } from 'react';
import './Dashboard.css';
import Deck from './Deck';
import Tabs from './Tabs';
import { useSocket } from '../../context/SocketContext';
import useDeckStates from '../../hooks/useDeckStates';
import useDeckScreenshot from '../../hooks/useDeckScreenshot';
import useDeviceSettings from '../../hooks/useDeviceSettings';
import useAdHocPlaylist from '../../hooks/useAdHocPlaylist';
import useScheduledQueue from '../../hooks/useScheduledQueue';
import VolumeControls from './VolumeControls';
import PlayerSettings from './PlayerSettings';
import MediaManagerSection from './MediaManagerSection';
import AdHocPlaylist from './AdHocPlaylist';
import ExternalAppMonitor from './ExternalAppMonitor';
import Overlays from '../Overlays';
import DefaultAds from '../DefaultAds/DefaultAds';
import TvRemotePanel from '../Controls/TvRemotePanel';
import { createScanId, debugPlaybackLog } from '../../utils/debugPlayback';
import axios from 'axios';
import { mergeIntegrationData } from '../../utils/deviceIntegration';

const buildDeckKey = (deck) => {
  if (!deck) return 'none';
  const id = deck.creativeId || deck.videoId || deck.contentId || '';
  return `${deck.type || 'unknown'}:${id}`;
};

const toDeckData = (item) => {
  if (!item) return {};
  const videoId = item.videoId || item.content_id || item.contentId || '';
  return {
    videoId,
    title: item.title || '',
    duration: item.duration || 0,
    currentTime: 0,
    type: item.type || item.content_type || 'youtube',
    thumbnailUrl:
      item.thumbnailUrl ||
      item.preview_api_url ||
      item.thumbnail_url ||
      (videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : null),
  };
};

const timeOfDayKey = () => {
  const hour = new Date().getHours();
  if (hour >= 17) return 'evening';
  if (hour >= 12) return 'afternoon';
  return 'morning';
};

const formatLiveQueuePluginLabel = (pluginKey) => (
  String(pluginKey || '').toLowerCase() === 'jukebox' ? 'Jukebox' : 'Karaoke'
);

const formatLiveQueuePhase = (phase) => {
  const normalized = String(phase || '').trim().toLowerCase();
  if (!normalized) return 'Unknown phase';
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatOverlayStatus = (overlay = {}) => {
  const parts = [];
  if (Array.isArray(overlay.positions) && overlay.positions.length) {
    parts.push(`Overlay: ${overlay.positions.join(', ')}`);
  } else {
    parts.push('Overlay: Not configured for this device');
  }
  if (overlay.transparency != null) {
    parts.push(`${overlay.transparency}% transparent`);
  }
  if (overlay.promotionQrSize != null) {
    parts.push(`QR ${overlay.promotionQrSize}%`);
  }
  return parts.join(' • ');
};

const hydrateDeckWithPlaylistMeta = (deck, playlistItems, fallbackItems) => {
  if (!deck || !deck.videoId) return deck || {};
  const matchesDeck = (item = {}) => {
    const candidateId =
      item.content_id ||
      item.contentId ||
      item.videoId ||
      (item.id && item.content_type !== 'youtube' ? item.id : null);
    const candidateCreativeId = item.creativeId || item.content_id || null;
    return (
      (candidateId && candidateId === deck.videoId) ||
      (deck.creativeId && candidateId && candidateId === deck.creativeId) ||
      (deck.creativeId && candidateCreativeId && candidateCreativeId === deck.creativeId)
    );
  };

  const candidate =
    (Array.isArray(playlistItems) && playlistItems.find(matchesDeck)) ||
    (Array.isArray(fallbackItems) && fallbackItems.find(matchesDeck));

  if (!candidate) return deck;

  return {
    ...deck,
    title: deck.title || candidate.title || '',
    thumbnailUrl:
      deck.thumbnailUrl ||
      candidate.preview_api_url ||
      candidate.thumbnail_url ||
      candidate.thumbnailUrl ||
      candidate.thumbnail_api_url ||
      null,
    type: deck.type || candidate.content_type || candidate.type || 'youtube',
    duration: deck.duration || candidate.duration || 0,
  };
};

function LiveQueueDeviceStatusBanner({ status, loading, error }) {
  const activeSessions = Array.isArray(status?.activeSessions) ? status.activeSessions : [];
  const primarySession = activeSessions[0] || null;
  const isActive = Boolean(primarySession);
  const pluginLabel = primarySession ? formatLiveQueuePluginLabel(primarySession.pluginKey) : null;
  const statusTitle = isActive
    ? `${pluginLabel} in use`
    : error ? 'Live queue status unavailable' : loading ? 'Checking live queue status...' : 'No live queue in use';
  const statusDetail = isActive
    ? `${primarySession.role?.label || 'Assigned device'} - ${primarySession.role?.detail || 'This device is assigned to the live queue.'}`
    : error
      ? 'The dashboard could not check this device. Refresh or rebuild the app service if this persists.'
      : 'This device is not assigned as a Jukebox or Karaoke player or promotional screen.';

  return (
    <div className={`live-queue-device-status ${isActive ? 'live-queue-device-status--active' : 'live-queue-device-status--idle'}`}>
      <div className="live-queue-device-status__main">
        <div className="live-queue-device-status__eyebrow">Device Status</div>
        <div className="live-queue-device-status__title">{statusTitle}</div>
        <div className="live-queue-device-status__detail">{statusDetail}</div>
      </div>
      {isActive ? (
        <div className="live-queue-device-status__meta">
          <span>{primarySession.eventInstanceName || `${pluginLabel} session #${primarySession.sessionId}`}</span>
          <span>{formatLiveQueuePhase(primarySession.currentPhase)}</span>
          <span>{formatOverlayStatus(primarySession.overlay)}</span>
        </div>
      ) : null}
    </div>
  );
}

function PlayerDashboard({
  device,
  deviceType,
  site,
  onRequestAssistantLogin,
  onDeviceUpdated,
  onSourceTargetSelected,
  requestedTab,
}) {
  const socket = useSocket();
  const nextId = useRef(1);

  const {
    deckA,
    deckB,
    isPlaying,
    isMuted,
    masterVolume,
    isMixing,
    playerSettings,
    interfaceMode,
    externalApp,
    setDeckA,
    setDeckB,
    setIsPlaying,
    setIsMuted,
    setMasterVolume,
    setPlayerSettings,
  } = useDeckStates(device);

  const [mixTriggeredAt, setMixTriggeredAt] = useState(null);
  const deckAScreenPreview = useDeckScreenshot(device?.id, deckA, mixTriggeredAt);
  useEffect(() => {
    setMixTriggeredAt(null);
  }, [device?.id]);

  const {
    fadeDuration,
    setFadeDuration,
    watermarkSize,
    setWatermarkSize,
    watermarkPosition,
    setWatermarkPosition,
    youtubePlayerCompatibilityMode,
    setYoutubePlayerCompatibilityMode,
    updateDeviceSetting,
    pauseBackgroundVideoOnVideoOverlay,
    overlayPauseSettingLoading,
    overlayPauseSettingSaving,
    overlayPauseSettingError,
    overlayPauseSettingWarning,
    overlayPauseSettingLoadFailed,
    saveVideoOverlayPauseSetting,
    reloadDeviceSettings,
  } = useDeviceSettings(device);

  const {
    adHocPlaylist,
    setAdHocPlaylist,
    fetchAdHocPlaylist,
    updateAdHocPlaylist,
  } = useAdHocPlaylist(device);
  const {
    scheduledQueue,
    scheduledMeta,
    fetchScheduledQueue,
    consumeNext: consumeScheduledNext,
  } = useScheduledQueue(device);

  const [selectedTab, setSelectedTab] = useState(requestedTab || 'Now Playing');
  useEffect(() => {
    if (requestedTab) setSelectedTab(requestedTab);
  }, [device?.id, requestedTab]);
  const [remainingTime, setRemainingTime] = useState(null);
  const [selectedPlaylistItem, setSelectedPlaylistItem] = useState(null);

  const lastDeckAKeyRef = useRef(null);
  const lastDeckBKeyRef = useRef(null);
  const [fallbackPlaylists, setFallbackPlaylists] = useState({});
  const deckAKey = useMemo(() => buildDeckKey(deckA), [deckA]);
  const deckBKey = useMemo(() => buildDeckKey(deckB), [deckB]);

  const [savedPlaylists, setSavedPlaylists] = useState([]);
  const [currentPlaylist, setCurrentPlaylist] = useState(null);
  const supportsOverlayAds = device?.type === 'TV' || device?.type === 'tv-control' || device?.type === 'stb';
  const [externalAppPanelTab, setExternalAppPanelTab] = useState('overlay-ads');
  const [playbackIssue, setPlaybackIssue] = useState(null);
  const [liveQueueStatus, setLiveQueueStatus] = useState(null);
  const [liveQueueStatusLoading, setLiveQueueStatusLoading] = useState(false);
  const [liveQueueStatusError, setLiveQueueStatusError] = useState(false);

  const token = localStorage.getItem('token');
  const [renameDeviceName, setRenameDeviceName] = useState(device?.name || '');
  const [savingAssistantSetup, setSavingAssistantSetup] = useState(false);

  useEffect(() => {
    setRenameDeviceName(device?.name || '');
  }, [device]);

  useEffect(() => {
    let cancelled = false;
    const fetchLiveQueueStatus = async () => {
      if (!device?.id || !token) {
        setLiveQueueStatus(null);
        setLiveQueueStatusError(false);
        return;
      }
      setLiveQueueStatusLoading(true);
      try {
        const response = await axios.get('/api/live-sessions/device-status', {
          params: { deviceId: device.id },
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setLiveQueueStatus(response.data || null);
          setLiveQueueStatusError(false);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching live queue device status:', error);
          setLiveQueueStatus(null);
          setLiveQueueStatusError(true);
        }
      } finally {
        if (!cancelled) setLiveQueueStatusLoading(false);
      }
    };

    fetchLiveQueueStatus();
    const interval = setInterval(fetchLiveQueueStatus, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [device?.id, token]);

  useEffect(() => {
    const fetchPlaylists = async () => {
      if (!device && !site) return;
      try {
        const siteId = site?.id || device?.site_id || device?.siteId || null;
        const areaId = device?.area_id || device?.areaId || null;
        const params = new URLSearchParams();
        if (siteId) params.set('siteId', siteId);
        if (areaId) params.set('areaId', areaId);
        const qs = params.toString() ? `?${params.toString()}` : '';
        const response = await axios.get(`/api/playlists${qs}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const playlists = response.data.playlists || [];
        const transformed = playlists.map((p) => ({
          ...p,
          tracksCount: p.items.length,
        }));
        setSavedPlaylists(transformed);
      } catch (error) {
        console.error('Error fetching playlists:', error);
      }
    };

    fetchPlaylists();
  }, [device, site, token]);

  const refreshAssistantConnectionStatus = async () => {
    const siteId = site?.id || null;
    if (!siteId) return false;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/googlehome/status?site=${siteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.google_assistant) {
        localStorage.setItem('assistantConnected', 'true');
      } else {
        localStorage.removeItem('assistantConnected');
      }
      if (res.data.google_home) {
        localStorage.setItem('googleConnected', 'true');
      } else {
        localStorage.removeItem('googleConnected');
      }
      return !!res.data.google_assistant;
    } catch (err) {
      console.error('Error fetching Google Assistant connection status:', err);
      return !!localStorage.getItem('assistantConnected');
    }
  };

  const enableAssistantTvControls = async (newName = null) => {
    if (!device?.id) return;
    const tvControls = {
      connected_methods: ['assistant'],
      selected_method: 'assistant',
    };
    const mergedIntegration = mergeIntegrationData(device.integration_data, {
      tv_controls: tvControls,
    });
    const settings = {
      integration_data: mergedIntegration,
    };
    if (typeof newName === 'string' && newName.trim()) {
      settings.name = newName.trim();
    }
    setSavingAssistantSetup(true);
    try {
      await axios.post(
        '/api/devices/update-settings',
        { deviceId: device.id, settings },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const updatedDevice = {
        ...device,
        ...(settings.name ? { name: settings.name } : {}),
        integration_data: mergedIntegration,
      };
      if (typeof onDeviceUpdated === 'function') onDeviceUpdated(updatedDevice);
    } catch (err) {
      console.error('Error enabling assistant TV controls:', err);
      alert('Failed to enable Google Assistant TV controls.');
    } finally {
      setSavingAssistantSetup(false);
    }
  };

  const onSavePlaylist = async (playlistItems, nameOverride = null) => {
    if (playlistItems.length === 0) {
      alert('Cannot save an empty playlist.');
      return;
    }

    const playlistName =
      typeof nameOverride === 'string' && nameOverride.trim()
        ? nameOverride.trim()
        : prompt('Enter a name for the new playlist:');
    if (playlistName) {
      try {
        const response = await axios.post(
          '/api/playlists',
          {
            name: playlistName,
            items: playlistItems,
            deviceType: deviceType,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        alert('Playlist saved!');
        const newPlaylist = {
          id: response.data.playlistId,
          name: playlistName,
          media_type: deviceType.toLowerCase(),
          items: playlistItems,
          tracksCount: playlistItems.length,
        };
        setSavedPlaylists([...savedPlaylists, newPlaylist]);
        setCurrentPlaylist(newPlaylist);
      } catch (error) {
        console.error('Error saving playlist:', error);
        alert('Failed to save playlist.');
      }
    }
  };

  const onUpdatePlaylist = async (updatedItems) => {
    if (!currentPlaylist) {
      alert('No playlist selected for updating.');
      return;
    }

    try {
      await axios.put(
        `/api/playlists/${currentPlaylist.id}`,
        {
          name: currentPlaylist.name,
          items: updatedItems,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Playlist updated successfully');
      setSavedPlaylists(
        savedPlaylists.map((p) =>
          p.id === currentPlaylist.id ? { ...p, items: updatedItems, tracksCount: updatedItems.length } : p
        )
      );
      setCurrentPlaylist({
        ...currentPlaylist,
        items: updatedItems,
      });
    } catch (error) {
      console.error('Error updating playlist:', error);
      alert('Failed to update playlist.');
    }
  };

  const onDeletePlaylist = async () => {
    if (!currentPlaylist) {
      alert('No playlist selected to delete.');
      return;
    }
    const playlistId = currentPlaylist.id;
    if (!window.confirm('Are you sure you want to delete this playlist?')) return;

    try {
      await axios.delete(`/api/playlists/${playlistId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      alert('Playlist deleted successfully');
      setSavedPlaylists(savedPlaylists.filter((p) => p.id !== playlistId));
      setCurrentPlaylist(null);
    } catch (error) {
      console.error('Error deleting playlist:', error);
      alert('Failed to delete playlist.');
    }
  };

  const onLoadSavedPlaylist = (p) => {
    setCurrentPlaylist({
      id: p.id,
      name: p.name,
      items: p.items || [],
    });
    if (p.items && p.items.length > 0) {
      updateAdHocPlaylist(p.items);
    }
  };

  const existingPlaylist = currentPlaylist ? currentPlaylist.items : [];

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return h !== '00' ? `${h}:${m}:${s}` : `${m}:${s}`;
  };

  const sendCommand = (command, value = null) => {
    if (!socket || !device) return;
    const id = nextId.current++;
    const payload = {
      deviceId: device.id,
      command: command,
      value: value,
      id,
    };
    debugPlaybackLog('sendCommand', payload);
    socket.emit('sendCommand', payload);
  };

  const handlePlayInSuitestream = async (item, remainingPlaylist = null) => {
    if (!item?.content_id) return;
    if (Array.isArray(remainingPlaylist)) {
      setAdHocPlaylist(remainingPlaylist);
      await updateAdHocPlaylist(remainingPlaylist);
    }
    sendCommand('launch_suitestream');
    window.setTimeout(() => {
      sendCommand('loadToDeckB', {
        videoId: item.content_id,
        title: item.title || '',
        source: 'dashboard:youtube-search',
        suppressSchedulerPrefetch: true,
      });
      window.setTimeout(() => {
        sendCommand('mixNow', {
          reason: 'dashboard:youtube-search',
          source: 'youtube-search',
        });
      }, 750);
    }, 1500);
  };

  const handlePlayPause = () => {
    const command = isPlaying ? 'pause' : 'play';
    sendCommand(command);
    setIsPlaying(!isPlaying);
  };

  const handleCue = () => {
    sendCommand('cue');
    setIsPlaying(false);
  };

  const handleMixNow = async () => {
    const triggeredAt = Date.now();
    debugPlaybackLog('UI Mix Now clicked', { deviceId: device?.id });
    if (device?.id) {
      try {
        const scanId = createScanId(device.id);
        debugPlaybackLog('Triggering scheduler scan-load-and-play (force) after Mix Now', {
          deviceId: device.id,
          scanId,
        });
        await axios.post(
          '/api/scheduler/scan-load-and-play',
          {
            deviceId: device.id,
            force: true,
            mixNow: true,
            trigger: 'webserver:dashboard-mix-now',
            scanId,
          },
          {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }
        );
        setMixTriggeredAt(triggeredAt);
        fetchScheduledQueue();
      } catch (err) {
        console.error('Failed to trigger scheduler scan-load-and-play', err);
      }
    }
  };

  const handleFadeDurationChange = (e) => {
    let value = parseInt(e.target.value);
    if (isNaN(value) || value < 0) return;
    if (value > 10) value = 10;
    setFadeDuration(value);
    updateDeviceSetting('fade_duration', value);
    sendCommand('setFadeDuration', { fadeDuration: value });
  };

  const handleSettingChange = (settingName, value) => {
    setPlayerSettings({
      ...playerSettings,
      [settingName]: value,
    });
    sendCommand('updateSettings', { [settingName]: value });
  };

  const handleWatermarkSizeChange = (nextSize) => {
    if (!nextSize) return;
    setWatermarkSize(nextSize);
    updateDeviceSetting('watermark_size', nextSize);
    sendCommand('setWatermarkDisplaySettings', { size: nextSize });
  };

  const handleWatermarkPositionChange = (nextPosition) => {
    if (!nextPosition) return;
    setWatermarkPosition(nextPosition);
    updateDeviceSetting('watermark_position', nextPosition);
    sendCommand('setWatermarkDisplaySettings', { position: nextPosition });
  };

  const handleYoutubePlayerCompatibilityModeChange = (nextMode) => {
    if (!['off', 'on', 'auto'].includes(nextMode)) return;
    setYoutubePlayerCompatibilityMode(nextMode);
    updateDeviceSetting('youtube_player_compatibility_mode', nextMode);
    sendCommand('setYoutubePlayerCompatibilityMode', { mode: nextMode });
  };

  const handleMasterVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    setMasterVolume(newVolume);
    sendCommand('setMasterVolume', { volume: newVolume });
  };

  const handleMuteUnmute = () => {
    const command = isMuted ? 'unmute' : 'mute';
    sendCommand(command);
    setIsMuted(!isMuted);
  };

  const handleAddToAdHocPlaylist = (newPlaylist) => {
    return updateAdHocPlaylist(newPlaylist);
  };

  const handleMoveUp = () => {
    if (selectedPlaylistItem === null || selectedPlaylistItem === 0) return;
    const newPlaylist = [...adHocPlaylist];
    [
      newPlaylist[selectedPlaylistItem - 1],
      newPlaylist[selectedPlaylistItem],
    ] = [
      newPlaylist[selectedPlaylistItem],
      newPlaylist[selectedPlaylistItem - 1],
    ];
    setAdHocPlaylist(newPlaylist);
    setSelectedPlaylistItem(selectedPlaylistItem - 1);
    updateAdHocPlaylist(newPlaylist);
  };

  const handleMoveDown = () => {
    if (
      selectedPlaylistItem === null ||
      selectedPlaylistItem === adHocPlaylist.length - 1
    )
      return;
    const newPlaylist = [...adHocPlaylist];
    [
      newPlaylist[selectedPlaylistItem + 1],
      newPlaylist[selectedPlaylistItem],
    ] = [
      newPlaylist[selectedPlaylistItem],
      newPlaylist[selectedPlaylistItem + 1],
    ];
    setAdHocPlaylist(newPlaylist);
    setSelectedPlaylistItem(selectedPlaylistItem + 1);
    updateAdHocPlaylist(newPlaylist);
  };

  const handleRemoveSelected = () => {
    if (selectedPlaylistItem === null) return;
    const newPlaylist = adHocPlaylist.filter(
      (_, index) => index !== selectedPlaylistItem
    );
    setAdHocPlaylist(newPlaylist);
    setSelectedPlaylistItem(null);
    updateAdHocPlaylist(newPlaylist);
  };

  const handleClearAdHoc = () => {
    setAdHocPlaylist([]);
    setSelectedPlaylistItem(null);
    updateAdHocPlaylist([]);
  };

  const handleLoadToUpNext = async () => {
    try {
      if (adHocPlaylist.length > 0) {
        const targetIndex = adHocPlaylist.findIndex(
          (item) => (item.content_type || 'youtube') === 'youtube'
        );
        if (targetIndex === -1) {
          alert('Only YouTube items can be sent directly to Up Next. Saved assets will play via the scheduler.');
          return;
        }
        const videoToLoad = adHocPlaylist[targetIndex];
        debugPlaybackLog('Loading ad-hoc YouTube item directly to DeckB', {
          deviceId: device?.id,
          contentId: videoToLoad?.content_id,
        });
        sendCommand('loadToDeckB', { videoId: videoToLoad.content_id });
        const updatedPlaylist = adHocPlaylist.filter((_, idx) => idx !== targetIndex);
        setAdHocPlaylist(updatedPlaylist);
        await updateAdHocPlaylist(updatedPlaylist);
        await fetchAdHocPlaylist();
      } else {
        debugPlaybackLog('Ad-hoc empty: triggering triggerScanAndLoad + consumeScheduledNext', {
          deviceId: device?.id,
        });
        const scanId = createScanId(device.id);
        sendCommand('triggerScanAndLoad', { force: false, scanId, reason: 'dashboard:LoadToUpNext-empty' });
        consumeScheduledNext();
      }
    } catch (error) {
      console.error('Error loading to up next:', error);
    }
  };

  useEffect(() => {
    if (!device?.id) return;
    const fetchFallbacks = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get('/api/playlists/fallback', {
          params: { deviceId: device.id },
          headers: { Authorization: `Bearer ${token}` },
        });
        setFallbackPlaylists(res.data || {});
      } catch (error) {
        console.error('Error fetching fallback playlists:', error);
      }
    };
    fetchFallbacks();
  }, [device?.id]);

  useEffect(() => {
    if (!device) return;
    if (deckAKey !== lastDeckAKeyRef.current || deckBKey !== lastDeckBKeyRef.current) {
      debugPlaybackLog('Deck key changed; refreshing ad-hoc + scheduled queue', {
        deviceId: device?.id,
        prevDeckAKey: lastDeckAKeyRef.current,
        prevDeckBKey: lastDeckBKeyRef.current,
        deckAKey,
        deckBKey,
      });
      lastDeckAKeyRef.current = deckAKey;
      lastDeckBKeyRef.current = deckBKey;
      fetchAdHocPlaylist();
      fetchScheduledQueue();
    }
  }, [device, deckAKey, deckBKey, fetchAdHocPlaylist, fetchScheduledQueue]);

  useEffect(() => {
    if (!device?.id) return undefined;
    fetchScheduledQueue();
    const interval = setInterval(fetchScheduledQueue, 15000);
    return () => clearInterval(interval);
  }, [device?.id, fetchScheduledQueue]);

  useEffect(() => {
    if (deckA && deckA.duration > 0) {
      setRemainingTime(deckA.duration - (deckA.currentTime || 0));
    } else {
      setRemainingTime(null);
    }
  }, [deckA]);

  useEffect(() => {
    if (!socket || !device?.id) return undefined;

    const handlePlaybackIssue = (payload = {}) => {
      if (payload.deviceId !== device.id) return;
      fetchAdHocPlaylist();
      const reason = payload.reason ? ` (${payload.reason})` : '';
      setPlaybackIssue(payload.message ? `${payload.message}${reason}` : `Skipped unplayable item${reason}`);
    };

    socket.on('devicePlaybackIssue', handlePlaybackIssue);
    return () => {
      socket.off('devicePlaybackIssue', handlePlaybackIssue);
    };
  }, [socket, device?.id, fetchAdHocPlaylist]);

  useEffect(() => {
    if (!playbackIssue) return undefined;
    const timer = setTimeout(() => setPlaybackIssue(null), 8000);
    return () => clearTimeout(timer);
  }, [playbackIssue]);

  const upNextDeckData = useMemo(() => {
    if (deckB && deckB.videoId) {
      const timeKey = timeOfDayKey();
      const fallbackList = fallbackPlaylists?.[timeKey] || [];
      return hydrateDeckWithPlaylistMeta(deckB, adHocPlaylist, fallbackList);
    }
    if (adHocPlaylist.length > 0) return toDeckData(adHocPlaylist[0]);
    if (scheduledQueue.length > 0) return toDeckData(scheduledQueue[0]);
    const timeKey = timeOfDayKey();
    const fallbackList = fallbackPlaylists?.[timeKey] || [];
    if (fallbackList.length > 0) return toDeckData(fallbackList[0]);
    return {};
  }, [deckB, adHocPlaylist, fallbackPlaylists, scheduledQueue]);

  const isExternalAppMode = interfaceMode === 'external_app' && externalApp;
  const activeLiveQueueSession = Array.isArray(liveQueueStatus?.activeSessions)
    ? liveQueueStatus.activeSessions[0] || null
    : null;
  const liveExperienceOwned = activeLiveQueueSession?.role?.exclusive === true;

  useEffect(() => {
    if (isExternalAppMode && selectedTab === 'Player Settings') {
      setSelectedTab('Now Playing');
    }
  }, [isExternalAppMode, selectedTab]);

  useEffect(() => {
    if (liveExperienceOwned && selectedTab === 'Player Settings') {
      setSelectedTab('Now Playing');
    }
  }, [liveExperienceOwned, selectedTab]);

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h2>Dashboard - {device.name}</h2>
      </div>

      {playbackIssue && <div className="playback-issue-banner">{playbackIssue}</div>}

      <Tabs
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        hidePlayerSettings={isExternalAppMode}
        liveExperienceOwned={liveExperienceOwned}
      />

      {selectedTab === 'Now Playing' && (
        <div className="now-playing-tab">
          <LiveQueueDeviceStatusBanner
            status={liveQueueStatus}
            loading={liveQueueStatusLoading}
            error={liveQueueStatusError}
          />

          {liveExperienceOwned ? (
            <div className="live-queue-device-status live-queue-device-status--active">
              This screen is exclusively controlled by {formatLiveQueuePluginLabel(activeLiveQueueSession.pluginKey)}.
              Use its runtime controls or stop the session before launching other content.
            </div>
          ) : null}

          {!liveExperienceOwned && (isExternalAppMode ? (
            <div className="external-app-now-playing">
              <div className="external-app-now-playing__actions">
                <button
                  type="button"
                  className="primary"
                  onClick={() => sendCommand('launch_suitestream')}
                >
                  Launch Suitestream
                </button>
                <span>
                  Return to Suitestream.tv, or launch another installed app directly below.
                </span>
              </div>
              <ExternalAppMonitor device={device} externalApp={externalApp} />
              <MediaManagerSection
                handleAddToAdHocPlaylist={handleAddToAdHocPlaylist}
                handleLoadToUpNext={handleLoadToUpNext}
                adHocPlaylist={adHocPlaylist}
                deviceType={deviceType}
                onSavePlaylist={onSavePlaylist}
                onUpdatePlaylist={onUpdatePlaylist}
                onDeletePlaylist={onDeletePlaylist}
                onLoadSavedPlaylist={onLoadSavedPlaylist}
                savedPlaylists={savedPlaylists}
                existingPlaylist={existingPlaylist}
                playlistName={currentPlaylist ? currentPlaylist.name : 'New Playlist'}
                showLoadToUpNextButton={false}
                showPlaylist={false}
                siteId={site?.id || null}
                site={site}
                selectedDevice={device}
                showOverlayAdsTab={false}
                overlayPlaybackMode="system"
                onPlayInSuitestream={handlePlayInSuitestream}
              />
              {supportsOverlayAds && (
                <div className="external-app-overlay-tools">
                  <div className="media-source-tabs external-app-overlay-tools__tabs">
                    <button
                      className={externalAppPanelTab === 'overlay-ads' ? 'active' : ''}
                      onClick={() => setExternalAppPanelTab('overlay-ads')}
                    >
                      Overlay Ads
                    </button>
                    <button
                      className={externalAppPanelTab === 'default-ads' ? 'active' : ''}
                      onClick={() => setExternalAppPanelTab('default-ads')}
                    >
                      Default Ads
                    </button>
                  </div>
                  <div className="overlay-ads-tab">
                    {externalAppPanelTab === 'overlay-ads' ? (
                      <Overlays
                        site={site}
                        selectedDevice={device}
                        playbackMode="system"
                      />
                    ) : (
                      <DefaultAds
                        site={site}
                        selectedDevice={device}
                        playbackMode="system"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              <div className="now-playing-layout">
                <div className="top-row">
                  <div className="deck-section left-deck">
                    <Deck deckData={deckA} deckLabel="A" screenPreview={deckAScreenPreview} />
                  </div>
                  <div className="center-section">
                    <div className="countdown-timers">
                      <p>
                        <strong>Remaining:</strong> {formatTime(remainingTime || 0)}
                      </p>
                    </div>
                  </div>
                  <div className="deck-section right-deck">
                    <Deck deckData={upNextDeckData} deckLabel="B" />
                  </div>
                </div>

                <div className="bottom-row">
                  <div className="volume-controls-row">
                    <VolumeControls
                      masterVolume={masterVolume}
                      handleMasterVolumeChange={handleMasterVolumeChange}
                      isMuted={isMuted}
                      handleMuteUnmute={handleMuteUnmute}
                      isPlaying={isPlaying}
                      handlePlayPause={handlePlayPause}
                    />
                  </div>
                  <div className="load-upnext-button">
                    <button
                      className="green-button"
                      onClick={() => onSavePlaylist(adHocPlaylist)}
                      disabled={adHocPlaylist.length === 0}
                    >
                      Save
                    </button>
                    <button
                      className="blue-button"
                      onClick={handleMixNow}
                      disabled={isMixing || deckA?.type === 'event'}
                    >
                      Mix Now
                    </button>
                    <button className="green-button" onClick={handleLoadToUpNext}>
                      Load to Up Next
                    </button>
                  </div>
                </div>
              </div>

              <div className="ad-hoc-section">
                <MediaManagerSection
                  handleAddToAdHocPlaylist={handleAddToAdHocPlaylist}
                  handleLoadToUpNext={handleLoadToUpNext}
                  adHocPlaylist={adHocPlaylist}
                  deviceType={deviceType}
                  onSavePlaylist={onSavePlaylist}
                  onUpdatePlaylist={onUpdatePlaylist}
                  onDeletePlaylist={onDeletePlaylist}
                  onLoadSavedPlaylist={onLoadSavedPlaylist}
                  savedPlaylists={savedPlaylists}
                  existingPlaylist={existingPlaylist}
                  playlistName={currentPlaylist ? currentPlaylist.name : 'New Playlist'}
                  showLoadToUpNextButton={false}
                  showPlaylist={false}
                  siteId={site?.id || null}
                  site={site}
                  selectedDevice={device}
                  showOverlayAdsTab={supportsOverlayAds}
                  overlayPlaybackMode="suitestream"
                  onPlayInSuitestream={handlePlayInSuitestream}
                />
                <div className="ad-hoc-side">
                  <AdHocPlaylist
                    adHocPlaylist={adHocPlaylist}
                    selectedPlaylistItem={selectedPlaylistItem}
                    setSelectedPlaylistItem={setSelectedPlaylistItem}
                    handleMoveUp={handleMoveUp}
                    handleMoveDown={handleMoveDown}
                    handleRemoveSelected={handleRemoveSelected}
                    handleClear={handleClearAdHoc}
                  />
                  {scheduledQueue.length > 0 && (
                    <div className={`scheduled-queue ${adHocPlaylist.length === 0 ? 'full' : ''}`}>
                      <h4>Scheduled Items</h4>
                      {scheduledMeta ? (
                        <div className="scheduled-meta">
                          <div className="scheduled-title">
                            Playlist #{scheduledMeta.item_id}{' '}
                            {scheduledMeta.randomize ? '(Randomized)' : '(Ordered)'}
                          </div>
                          <div className="scheduled-time">
                            Active {new Date(scheduledMeta.start_time).toLocaleString()} –{' '}
                            {new Date(scheduledMeta.end_time).toLocaleString()}
                          </div>
                        </div>
                      ) : (
                        <div className="scheduled-empty">No scheduled playlist active.</div>
                      )}
                      <ol className="scheduled-list">
                        {scheduledQueue.slice(0, 3).map((item, idx) => (
                          <li key={`${item.id || item.content_id}-${idx}`} className="scheduled-item">
                            <span className="scheduled-item-title">{item.title || item.content_id}</span>
                            <span className="scheduled-item-duration">
                              {item.duration ? `${Math.round(item.duration)}s` : ''}
                            </span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              </div>
            </>
          ))}
        </div>
      )}

      {selectedTab === 'Player Settings' && (
        <div className="player-settings-tab">
          <PlayerSettings
            playerSettings={playerSettings}
            onSettingChange={handleSettingChange}
            fadeDuration={fadeDuration}
            handleFadeDurationChange={handleFadeDurationChange}
            watermarkSize={watermarkSize}
            watermarkPosition={watermarkPosition}
            youtubePlayerCompatibilityMode={youtubePlayerCompatibilityMode}
            onWatermarkSizeChange={handleWatermarkSizeChange}
            onWatermarkPositionChange={handleWatermarkPositionChange}
            onYoutubePlayerCompatibilityModeChange={handleYoutubePlayerCompatibilityModeChange}
            pauseBackgroundVideoOnVideoOverlay={pauseBackgroundVideoOnVideoOverlay}
            overlayPauseSettingLoading={overlayPauseSettingLoading}
            overlayPauseSettingSaving={overlayPauseSettingSaving}
            overlayPauseSettingError={overlayPauseSettingError}
            overlayPauseSettingWarning={overlayPauseSettingWarning}
            overlayPauseSettingLoadFailed={overlayPauseSettingLoadFailed}
            onVideoOverlayPauseSettingChange={saveVideoOverlayPauseSetting}
            onRetryDeviceSettings={reloadDeviceSettings}
          />
        </div>
      )}

      {selectedTab === 'TV Remote' && <TvRemotePanel device={device} onDeviceUpdated={onDeviceUpdated} onSourceTargetSelected={onSourceTargetSelected} />}
    </div>
  );
}

// Devices without a Suitestream player still use the same Dashboard remote.
function Dashboard(props) {
  if (['tv-control', 'stb', 'lgtv'].includes(props.deviceType)) {
    return <div className="dashboard">
      <div className="dashboard-header"><h2>Dashboard - {props.device.name}</h2></div>
      <div className="dashboard-tabs"><button type="button" className="tab-button active">TV Remote</button></div>
      <TvRemotePanel device={props.device} onDeviceUpdated={props.onDeviceUpdated} onSourceTargetSelected={props.onSourceTargetSelected} />
    </div>;
  }
  return <PlayerDashboard {...props} />;
}

export default Dashboard;
